// client/src/utils/api.ts
export { apiRequest } from './dashboardApi';

import { getAuthHeaders } from './authHeaders';
import {
  CSRF_HEADER,
  ensureCsrfToken,
  getCsrfToken,
  isStateChangingMethod,
} from './csrfToken';

/**
 * `fetch` with the app's auth attached, for call sites that need the raw
 * Response - status codes, content-type sniffing, bespoke error bodies - and
 * so cannot use `apiRequest`, which resolves to the parsed body and throws on
 * any non-2xx.
 *
 * Adds what every authenticated request needs and nothing else:
 *   - `credentials: 'include'`, so the HttpOnly `auth_session` cookie is sent.
 *     This is the part that matters: the session is no longer readable from
 *     JS, so a hand-built `Authorization` header is not available after a hard
 *     navigation, and without cookies the request goes out anonymous.
 *   - `getAuthHeaders()`, which still supplies a bearer token opportunistically
 *     when a Supabase session exists.
 *   - the CSRF token on state-changing methods, which the server requires of
 *     any cookie-authenticated mutation.
 *
 * Call-site headers win: anything already set is left alone.
 */
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);

  for (const [key, value] of Object.entries(getAuthHeaders())) {
    // Never force a Content-Type onto a FormData body - the browser has to set
    // it itself so the multipart boundary is included.
    if (key.toLowerCase() === 'content-type' && init.body instanceof FormData) continue;
    if (!headers.has(key)) headers.set(key, value);
  }

  if (isStateChangingMethod(init.method)) {
    const csrfToken = getCsrfToken() ?? (await ensureCsrfToken());
    if (csrfToken && !headers.has(CSRF_HEADER)) headers.set(CSRF_HEADER, csrfToken);
  }

  return fetch(input, { ...init, headers, credentials: 'include' });
}
